import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAnuncios } from '../hooks/useAnuncios';
import { Categoria, FiltrosAnuncio } from '../types';
import AnuncioGrid from '../components/anuncios/AnuncioGrid';
import FiltrosAnuncioComponent from '../components/anuncios/FiltrosAnuncio';
import Pagination from '../components/common/Pagination';
import Button from '../components/common/Button';

const Anuncios: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  
  const {
    anuncios,
    filtros,
    total,
    pagina,
    totalPaginas,
    isLoading,
    isLoadingMais,
    listarAnuncios,
    setFiltros,
    limparFiltros,
    carregarMais,
  } = useAnuncios();

  // Initialize filters from URL params
  useEffect(() => {
    const initialFiltros: FiltrosAnuncio = {
      busca: searchParams.get('busca') || undefined,
      categoria: (searchParams.get('categoria') as Categoria) || undefined,
      subcategoria: searchParams.get('subcategoria') || undefined,
      precoMin: searchParams.get('precoMin') ? Number(searchParams.get('precoMin')) : undefined,
      precoMax: searchParams.get('precoMax') ? Number(searchParams.get('precoMax')) : undefined,
      estado: searchParams.get('estado') || undefined,
      cidade: searchParams.get('cidade') || undefined,
      ordenar: (searchParams.get('ordenar') as FiltrosAnuncio['ordenar']) || 'recentes',
      pagina: 1,
      limite: 12,
    };
    
    setFiltros(initialFiltros);
    listarAnuncios(initialFiltros);
  }, []);

  // Update URL when filters change
  const handleApplyFilters = () => {
    const params = new URLSearchParams();
    
    if (filtros.busca) params.set('busca', filtros.busca);
    if (filtros.categoria) params.set('categoria', filtros.categoria);
    if (filtros.subcategoria) params.set('subcategoria', filtros.subcategoria);
    if (filtros.precoMin) params.set('precoMin', filtros.precoMin.toString());
    if (filtros.precoMax) params.set('precoMax', filtros.precoMax.toString());
    if (filtros.estado) params.set('estado', filtros.estado);
    if (filtros.cidade) params.set('cidade', filtros.cidade);
    if (filtros.ordenar && filtros.ordenar !== 'recentes') params.set('ordenar', filtros.ordenar);
    
    setSearchParams(params);
    listarAnuncios(filtros);
  };

  const handleClearFilters = () => {
    limparFiltros();
    setSearchParams({});
    listarAnuncios();
  };

  const handlePageChange = (newPage: number) => {
    const newFiltros = { ...filtros, pagina: newPage };
    setFiltros(newFiltros);
    listarAnuncios(newFiltros);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Count active filters
  const activeFiltersCount = [
    filtros.busca,
    filtros.categoria,
    filtros.subcategoria,
    filtros.precoMin,
    filtros.precoMax,
    filtros.estado,
    filtros.cidade,
  ].filter(Boolean).length;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="container-custom py-6">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
            Anúncios
          </h1>
          <p className="text-gray-600">
            {total > 0 ? (
              <>
                <span className="font-semibold">{total.toLocaleString('pt-BR')}</span> anúncios encontrados
              </>
            ) : (
              'Explore nossos anúncios'
            )}
          </p>
        </div>
      </div>

      <div className="container-custom py-8">
        <div className="flex gap-8">
          {/* Desktop Filters Sidebar */}
          <aside className="hidden lg:block w-80 flex-shrink-0">
            <div className="sticky top-24">
              <FiltrosAnuncioComponent
                filtros={filtros}
                onChange={setFiltros}
                onApply={handleApplyFilters}
                onClear={handleClearFilters}
              />
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            {/* Mobile Filter Toggle & Sort */}
            <div className="flex items-center justify-between mb-6 gap-4">
              {/* Mobile Filter Button */}
              <Button
                variant="outline"
                onClick={() => setShowMobileFilters(true)}
                className="lg:hidden"
                leftIcon={
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                  </svg>
                }
              >
                Filtros
                {activeFiltersCount > 0 && (
                  <span className="ml-1 w-5 h-5 bg-green-600 text-white text-xs rounded-full flex items-center justify-center">
                    {activeFiltersCount}
                  </span>
                )}
              </Button>

              {/* Sort Dropdown - Desktop */}
              <div className="hidden sm:flex items-center gap-2 ml-auto">
                <span className="text-sm text-gray-600">Ordenar:</span>
                <select
                  value={filtros.ordenar || 'recentes'}
                  onChange={(e) => {
                    const newFiltros = { ...filtros, ordenar: e.target.value as FiltrosAnuncio['ordenar'] };
                    setFiltros(newFiltros);
                    listarAnuncios(newFiltros);
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none"
                >
                  <option value="recentes">Mais recentes</option>
                  <option value="relevancia">Relevância</option>
                  <option value="preco_asc">Menor preço</option>
                  <option value="preco_desc">Maior preço</option>
                </select>
              </div>

              {/* View Toggle */}
              <div className="flex items-center bg-gray-100 rounded-lg p-1">
                <button className="p-2 bg-white rounded-lg shadow-sm text-green-700">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                </button>
                <button className="p-2 text-gray-400 hover:text-gray-600">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Active Filters Pills */}
            {activeFiltersCount > 0 && (
              <div className="flex flex-wrap items-center gap-2 mb-6">
                <span className="text-sm text-gray-600">Filtros ativos:</span>
                {filtros.busca && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                    Busca: "{filtros.busca}"
                    <button onClick={() => { setFiltros({ ...filtros, busca: '' }); handleApplyFilters(); }}>
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </span>
                )}
                {filtros.categoria && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                    {filtros.categoria}
                    <button onClick={() => { setFiltros({ ...filtros, categoria: undefined, subcategoria: undefined }); handleApplyFilters(); }}>
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </span>
                )}
                {filtros.estado && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                    {filtros.estado}
                    <button onClick={() => { setFiltros({ ...filtros, estado: undefined, cidade: undefined }); handleApplyFilters(); }}>
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </span>
                )}
                <button
                  onClick={handleClearFilters}
                  className="text-sm text-red-600 hover:text-red-700 font-medium"
                >
                  Limpar todos
                </button>
              </div>
            )}

            {/* Anuncios Grid */}
            <AnuncioGrid
              anuncios={anuncios}
              isLoading={isLoading}
              columns={3}
              emptyMessage="Nenhum anúncio encontrado"
              emptyAction={
                <Button onClick={handleClearFilters} variant="outline">
                  Limpar filtros
                </Button>
              }
            />

            {/* Pagination */}
            {totalPaginas > 1 && (
              <div className="mt-8">
                <Pagination
                  currentPage={pagina}
                  totalPages={totalPaginas}
                  onPageChange={handlePageChange}
                  totalItems={total}
                  itemsPerPage={filtros.limite || 12}
                />
              </div>
            )}
          </main>
        </div>
      </div>

      {/* Mobile Filters Modal */}
      {showMobileFilters && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowMobileFilters(false)} />
          <div className="absolute inset-y-0 left-0 w-full max-w-sm bg-white shadow-xl overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-4 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900">Filtros</h2>
              <button
                onClick={() => setShowMobileFilters(false)}
                className="p-2 text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <FiltrosAnuncioComponent
              filtros={filtros}
              onChange={setFiltros}
              onApply={handleApplyFilters}
              onClear={handleClearFilters}
              isMobile
              onClose={() => setShowMobileFilters(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Anuncios;
